import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ACCREDITATION_META } from "../../lib/format";
import { REGION_NAMES, comunasOf } from "../../lib/chile";
import { MultiSelect } from "../../components/MultiSelect";
import { useAuth } from "../../context/AuthContext";
import { Truck, FileText, MapPin, Plus, Trash, CircleNotch, PaperPlaneTilt, CheckCircle, UploadSimple, X, ArrowRight } from "@phosphor-icons/react";

export default function CarrierOnboarding() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [config, setConfig] = useState({ vehicleTypes: [], zones: [], categories: [], documentTypes: [] });
  const [busy, setBusy] = useState(false);
  const [regions, setRegions] = useState([]);
  const [docTab, setDocTab] = useState("conductor");

  const load = () => api.get("/carrier/profile").then((r) => setProfile(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  // Deriva las regiones activas a partir de las comunas ya seleccionadas.
  useEffect(() => {
    if (profile && regions.length === 0) {
      const zones = profile.operatingZones || [];
      if (zones.length) setRegions(REGION_NAMES.filter((r) => comunasOf(r).some((c) => zones.includes(c))));
    }
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRegion = (r) => setRegions((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  const comunaOptions = regions.flatMap((r) => comunasOf(r));

  const toggle = (field, val) => {
    const cur = profile[field] || [];
    const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
    setProfile({ ...profile, [field]: next });
  };

  const saveProfile = async () => {
    await api.put("/carrier/profile", { serviceTypes: profile.serviceTypes, operatingZones: profile.operatingZones });
    toast.success("Perfil guardado");
  };

  const saveCompliance = async () => {
    try {
      await api.put("/carrier/profile", {
        serviceTypes: profile.serviceTypes, operatingZones: profile.operatingZones,
        companyRut: profile.companyRut || "", rnstcNumber: profile.rnstcNumber || "",
        licenseClass: profile.licenseClass || "", licenseExpiry: profile.licenseExpiry || "",
        healthDeclaration: !!profile.healthDeclaration,
      });
      toast.success("Datos de acreditación guardados");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };


  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/carrier/submit");
      toast.success("¡Enviado a revisión! Un administrador revisará tu acreditación.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  if (!profile) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;
  const meta = ACCREDITATION_META[profile.accreditationStatus] || {};
  const driverName = `${user?.name || ""} ${user?.lastName || ""}`.trim();
  const knownTypes = new Set([...(config.driverDocs || []), ...(config.vehicleDocs || [])]);
  const otherDocs = (profile.documents || []).filter((d) => !knownTypes.has(d.documentType));

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-5" data-testid="carrier-onboarding">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Acreditación</span>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Mi perfil de transportista</h1>
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${meta.color}`} data-testid="accreditation-status">{meta.label}</span>
      </div>

      {/* Service types & zones */}
      <Section title="Tipos de servicio" icon={FileText}>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Selecciona uno o varios</label>
        <div className="mt-1.5">
          <MultiSelect testid="service-types-select" options={config.categories}
            selected={profile.serviceTypes || []} onToggle={(v) => toggle("serviceTypes", v)}
            placeholder="Selecciona tipos de servicio" />
        </div>
        {(profile.serviceTypes || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="selected-services">
            {profile.serviceTypes.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#FFD9CF] text-[#E04B2A] rounded-full pl-3 pr-2 py-1">
                {s}
                <button onClick={() => toggle("serviceTypes", s)} data-testid={`remove-service-${s}`} className="hover:text-red-600"><X size={13} weight="bold" /></button>
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section title="Zonas donde operas" icon={MapPin}>
        <p className="text-sm text-zinc-500 mb-3">Selecciona una o varias regiones y luego marca las comunas donde ofreces tus servicios.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Regiones</label>
            <div className="mt-1.5">
              <MultiSelect testid="zone-region-select" options={REGION_NAMES}
                selected={regions} onToggle={toggleRegion} placeholder="Selecciona regiones" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Comunas</label>
            <div className="mt-1.5">
              <MultiSelect testid="zone-comuna-select" options={comunaOptions} disabled={regions.length === 0}
                selected={profile.operatingZones || []} onToggle={(c) => toggle("operatingZones", c)}
                placeholder={regions.length ? "Selecciona comunas" : "Primero elige regiones"} />
            </div>
          </div>
        </div>

        {(profile.operatingZones || []).length > 0 && (
          <div className="mt-4 rounded-xl bg-zinc-50 border border-zinc-200 p-3" data-testid="selected-zones">
            <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500 mb-2">Comunas seleccionadas ({profile.operatingZones.length})</div>
            <div className="flex flex-wrap gap-2">
              {profile.operatingZones.map((z) => (
                <span key={z} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#FFD9CF] text-[#E04B2A] rounded-full pl-3 pr-2 py-1">
                  {z}
                  <button onClick={() => toggle("operatingZones", z)} data-testid={`remove-zone-${z}`} className="hover:text-red-600"><X size={13} weight="bold" /></button>
                </span>
              ))}
            </div>
          </div>
        )}

        <button onClick={saveProfile} data-testid="save-profile" className="mt-4 px-5 py-2.5 rounded-full bg-[#0F1115] text-white text-sm font-semibold hover:bg-[#1A1D24] transition-colors active:scale-95">
          Guardar servicios y zonas
        </button>
      </Section>

      <Section title="Empresa, vehículos y conductores" icon={FileText}>
        <p className="text-sm text-zinc-500 mb-3">Los datos de tu empresa, tus vehículos y tus conductores se administran en secciones propias del menú, cada una con sus documentos de acreditación.</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/app/carrier/company" data-testid="go-company"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F1115] text-white text-sm font-semibold hover:bg-[#1A1D24] transition-colors active:scale-95">
            Ir a Mi Empresa <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/app/carrier/vehicles" data-testid="go-vehicles"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 text-sm font-semibold hover:border-[#0F1115] transition-colors active:scale-95">
            Ir a Vehículos <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/app/carrier/drivers" data-testid="go-drivers"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 text-sm font-semibold hover:border-[#0F1115] transition-colors active:scale-95">
            Ir a Conductores <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </Section>

      {["PENDIENTE", "RECHAZADO"].includes(profile.accreditationStatus) && (
        <button onClick={submit} disabled={busy} data-testid="submit-accreditation"
          className="w-full py-3 rounded-xl bg-[#0F1115] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#1A1D24] transition-colors active:scale-95 disabled:opacity-60">
          {busy ? <CircleNotch size={20} className="animate-spin" /> : <><PaperPlaneTilt size={20} weight="bold" /> Enviar acreditación a revisión</>}
        </button>
      )}
      {profile.accreditationStatus === "EN_REVISION" && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center text-amber-700 font-semibold">Tu acreditación está en revisión.</div>
      )}
      {profile.accreditationStatus === "APROBADO" && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center text-emerald-700 font-semibold flex items-center justify-center gap-2">
          <CheckCircle size={20} weight="fill" /> ¡Estás acreditado! Ya puedes tomar servicios.
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4">
      <h2 className="font-semibold flex items-center gap-2 mb-3"><Icon size={20} className="text-[#FF5A36]" /> {title}</h2>
      {children}
    </div>
  );
}
function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all ${active ? "border-[#FF5A36] bg-[#FFF4F1] text-[#FF5A36]" : "border-zinc-200 hover:border-zinc-300"}`}>
      {children}
    </button>
  );
}
function VInput({ testid, ...props }) {
  return <input data-testid={testid} {...props} className="h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />;
}

const DOC_STATUS = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  APROBADO: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700" },
  RECHAZADO: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};
function expInfo(d) {
  if (!d.expirationDate) return null;
  const days = Math.ceil((new Date(d.expirationDate) - new Date()) / 86400000);
  if (isNaN(days)) return null;
  if (days < 0) return { label: "Vencido", cls: "bg-red-100 text-red-700" };
  if (days <= 15) return { label: `Vence en ${days}d`, cls: "bg-amber-100 text-amber-700" };
  return { label: `Vence ${String(d.expirationDate).slice(0, 10)}`, cls: "bg-zinc-100 text-zinc-600" };
}

function DocChecklistRow({ type, required, needsExpiry, docs, uploading, onUpload, vehicleId = null }) {
  const [expiry, setExpiry] = useState("");
  const fileRef = useRef(null);
  const doc = docs.filter((d) => d.documentType === type)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
  const exp = doc ? expInfo(doc) : null;
  const st = doc ? (DOC_STATUS[doc.status] || DOC_STATUS.PENDIENTE) : null;
  const busy = uploading === `${type}:${vehicleId || ""}`;

  return (
    <div className="rounded-xl border border-zinc-200 px-4 py-3" data-testid={`doc-row-${type}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-2">
          <FileText size={18} className="text-zinc-400" /> {type}
          {required && <span className="text-[10px] font-bold uppercase tracking-wide text-[#E04B2A]">Obligatorio</span>}
        </span>
        <div className="flex items-center gap-1.5">
          {exp && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${exp.cls}`}>{exp.label}</span>}
          {st ? <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
            : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Falta</span>}
        </div>
      </div>
      {doc?.status === "RECHAZADO" && doc.rejectionReason && <p className="text-xs text-red-600 mt-1.5">Motivo: {doc.rejectionReason}</p>}
      <div className="flex items-center gap-2 mt-2">
        {needsExpiry && (
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid={`doc-expiry-${type}`}
            className="h-9 px-3 rounded-lg border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
        )}
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
          onChange={(e) => {
            const f = e.target.files[0];
            if (needsExpiry && !expiry) { toast.error("Indica la fecha de vencimiento primero"); e.target.value = ""; return; }
            onUpload(type, expiry, f, vehicleId); e.target.value = "";
          }} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`upload-doc-${type}`}
          className="h-9 px-4 rounded-lg border border-dashed border-zinc-400 text-sm font-semibold flex items-center gap-1.5 hover:border-[#FF5A36] transition-colors disabled:opacity-60">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} {doc ? "Reemplazar" : "Subir"}
        </button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-[#0F1115] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
      {children}
    </button>
  );
}
